// ** Type Imports
import {
	type BillingPeriod,
	type CustomPlanType,
	type CustomPrice,
	type CustomPriceTier,
	type TiersMode
} from '../model/types'

const CENTS_IN_UNIT = 100

// The product only sells in USD, so the currency is never an admin decision.
export const DEFAULT_CURRENCY = 'usd'

// Every price created from now on is graduated; only legacy prices are volume.
export const DEFAULT_TIERS_MODE: TiersMode = 'graduated'

/**
 * Assignment is refused while the customer holds any of these. Note that a
 * subscription set to cancel at period end keeps reporting `active` until the
 * period actually ends, so it blocks too.
 */
export const BLOCKING_SUBSCRIPTION_STATUSES = [
	'active',
	'trialing',
	'past_due',
	'unpaid'
]

export const isAssignmentBlocked = (
	subscriptionStatus: string | null | undefined
): boolean =>
	subscriptionStatus !== null &&
	subscriptionStatus !== undefined &&
	BLOCKING_SUBSCRIPTION_STATUSES.includes(subscriptionStatus.toLowerCase())

export const PLAN_TYPES: CustomPlanType[] = ['FLAT_RATE', 'USAGE_BASED']

export const BILLING_PERIODS: BillingPeriod[] = [
	'MONTHLY',
	'SEMI_ANNUAL',
	'YEARLY'
]

export const PLAN_TYPE_LABELS: Record<CustomPlanType, string> = {
	FLAT_RATE: 'Flat rate',
	USAGE_BASED: 'Usage based'
}

export const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
	MONTHLY: 'Monthly',
	SEMI_ANNUAL: 'Every 6 months',
	YEARLY: 'Yearly'
}

export const TIERS_MODE_LABELS: Record<TiersMode, string> = {
	graduated: 'Graduated',
	volume: 'Volume'
}

export const getPlanTypeLabel = (planType: CustomPlanType | null): string =>
	planType ? PLAN_TYPE_LABELS[planType] : '—'

export const getBillingPeriodLabel = (
	billingPeriod: BillingPeriod | null
): string => (billingPeriod ? BILLING_PERIOD_LABELS[billingPeriod] : '—')

export const getTiersModeLabel = (tiersMode: TiersMode | null): string =>
	tiersMode ? TIERS_MODE_LABELS[tiersMode] : '—'

/**
 * The API speaks in the smallest currency unit, the form speaks in whole units.
 */
export const centsToUnits = (cents: number): number => cents / CENTS_IN_UNIT

export const unitsToCents = (units: string | number): number =>
	Math.round(Number(units) * CENTS_IN_UNIT)

export const formatMoneyFromCents = (
	cents: number | null | undefined,
	currency = DEFAULT_CURRENCY
): string => {
	if (cents === null || cents === undefined) return '—'

	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(centsToUnits(cents))
}

/**
 * Human label for a tier's client range, e.g. "1 – 10" or "51 and above".
 */
export const formatTierRange = (
	tier: CustomPriceTier,
	previousUpTo: number | null
): string => {
	const from = (previousUpTo ?? 0) + 1

	if (tier.upTo === null) return `${from} and above`
	if (tier.upTo === from) return `${from}`

	return `${from} – ${tier.upTo}`
}

/**
 * One line of a price breakdown: the clients that fall in a tier and what they
 * cost. Under `volume` there is only ever one line.
 */
export interface TierChargeLine {
	rangeLabel: string
	units: number
	unitAmount: number | null
	flatAmount: number | null
	amountInCents: number
}

/**
 * Upper bound of every tier, with the last one unbounded. Returns null when the
 * tiers are not yet a usable ladder — which happens while a tier editor is being
 * filled in — so callers can render a placeholder instead of NaN.
 */
const getTierBounds = (tiers: CustomPriceTier[]): number[] | null => {
	const bounds: number[] = []

	for (let index = 0; index < tiers.length; index++) {
		// the last tier is always the unbounded one
		if (index === tiers.length - 1) {
			bounds.push(Number.POSITIVE_INFINITY)
			break
		}

		const { upTo } = tiers[index]
		if (upTo === null || !Number.isInteger(upTo) || upTo <= 0) return null
		if (bounds.length > 0 && upTo <= bounds[bounds.length - 1]) return null

		bounds.push(upTo)
	}

	return bounds
}

const buildChargeLine = (
	tier: CustomPriceTier,
	units: number,
	previousUpTo: number | null
): TierChargeLine => ({
	rangeLabel: formatTierRange(tier, previousUpTo),
	units,
	unitAmount: tier.unitAmount,
	flatAmount: tier.flatAmount,
	// a tier's flat fee is charged once for the tier, on top of its per-unit rate
	amountInCents: (tier.flatAmount ?? 0) + (tier.unitAmount ?? 0) * units
})

/**
 * Splits a client count across a price's tiers.
 *
 * `graduated` charges each portion of the count at its own tier's rate, so 15
 * clients on `1–10 @ $10` + `11–inf @ $8` cost (10 × $10) + (5 × $8) = $140.
 * `volume` charges the whole count at the single tier it lands in, so the same
 * 15 clients cost 15 × $8 = $120.
 *
 * Returns an empty list when no total can be computed.
 */
export const getTierBreakdown = (
	tiers: CustomPriceTier[],
	quantity: number,
	tiersMode: TiersMode
): TierChargeLine[] => {
	if (tiers.length === 0) return []
	if (!Number.isInteger(quantity) || quantity <= 0) return []

	const bounds = getTierBounds(tiers)
	if (bounds === null) return []

	if (tiersMode === 'volume') {
		const index = bounds.findIndex((bound) => quantity <= bound)
		if (index === -1) return []

		return [
			buildChargeLine(
				tiers[index],
				quantity,
				index > 0 ? bounds[index - 1] : null
			)
		]
	}

	const lines: TierChargeLine[] = []
	let previousBound = 0

	for (let index = 0; index < bounds.length; index++) {
		const bound = bounds[index]
		const units = Math.min(quantity, bound) - previousBound

		if (units > 0) {
			lines.push(
				buildChargeLine(tiers[index], units, index > 0 ? previousBound : null)
			)
		}

		previousBound = bound
		if (quantity <= bound) break
	}

	return lines
}

export const calculateTierTotalInCents = (
	tiers: CustomPriceTier[],
	quantity: number,
	tiersMode: TiersMode
): number | null => {
	const lines = getTierBreakdown(tiers, quantity, tiersMode)
	if (lines.length === 0) return null

	return lines.reduce((total, line) => total + line.amountInCents, 0)
}

/**
 * Short "amount" summary used in the prices table.
 */
export const formatPriceAmount = (price: CustomPrice): string => {
	if (price.planType === 'FLAT_RATE') {
		return formatMoneyFromCents(price.amount, price.currency)
	}

	const tiers = price.tiers ?? []
	if (tiers.length === 0) return '—'

	const unitAmounts = tiers
		.map(({ unitAmount }) => unitAmount)
		.filter((unitAmount): unitAmount is number => unitAmount !== null)

	if (unitAmounts.length === 0) {
		return `${tiers.length} ${tiers.length === 1 ? 'tier' : 'tiers'}`
	}

	const lowest = formatMoneyFromCents(Math.min(...unitAmounts), price.currency)
	const highest = formatMoneyFromCents(Math.max(...unitAmounts), price.currency)
	const range = lowest === highest ? lowest : `${lowest} – ${highest}`

	return `${range} / client · ${tiers.length} ${
		tiers.length === 1 ? 'tier' : 'tiers'
	}`
}
